import React, { useState, useEffect } from 'react';
import { Crown, Zap, Shield, Castle, Sparkles } from 'lucide-react';

const WildChessApp = () => {
  const [board, setBoard] = useState(null);
  const [selectedSquare, setSelectedSquare] = useState(null);
  const [currentPlayer, setCurrentPlayer] = useState('white');
  const [message, setMessage] = useState('');
  const [validMoves, setValidMoves] = useState([]);
  const [capturedPieces, setCapturedPieces] = useState({ white: [], black: [] });
  const [gameStarted, setGameStarted] = useState(false);
  const [playerColor, setPlayerColor] = useState('white');
  const [difficulty, setDifficulty] = useState(1);
  const [isThinking, setIsThinking] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [winner, setWinner] = useState(null);

  useEffect(() => {
    if (gameStarted && currentPlayer !== playerColor && !isThinking && !gameOver) {
      setIsThinking(true);
      setMessage('Wild thing is thinking...');
      setTimeout(() => {
        makeComputerMove();
      }, 1500);
    }
  }, [currentPlayer, gameStarted, playerColor, isThinking, gameOver]);

  function initializeBoard() {
    const board = Array(8).fill(null).map(() => Array(8).fill(null));
    
    // Pawns
    for (let i = 0; i < 8; i++) {
      board[1][i] = { type: 'pawn', color: 'black' };
      board[6][i] = { type: 'pawn', color: 'white' };
    }
    
    // Rooks
    board[0][0] = board[0][7] = { type: 'rook', color: 'black' };
    board[7][0] = board[7][7] = { type: 'rook', color: 'white' };
    
    // Knights
    board[0][1] = board[0][6] = { type: 'knight', color: 'black' };
    board[7][1] = board[7][6] = { type: 'knight', color: 'white' };
    
    // Bishops
    board[0][2] = board[0][5] = { type: 'bishop', color: 'black' };
    board[7][2] = board[7][5] = { type: 'bishop', color: 'white' };
    
    // Queens
    board[0][3] = { type: 'queen', color: 'black' };
    board[7][3] = { type: 'queen', color: 'white' };
    
    // Kings
    board[0][4] = { type: 'king', color: 'black' };
    board[7][4] = { type: 'king', color: 'white' };
    
    return board;
  }

  const pieceEmojis = {
    king: { white: '♔', black: '♚' },
    queen: { white: '♕', black: '♛' },
    rook: { white: '♖', black: '♜' },
    bishop: { white: '♗', black: '♝' },
    knight: { white: '♘', black: '♞' },
    pawn: { white: '♙', black: '♟' }
  };

  const pieceNames = {
    king: 'King',
    queen: 'Queen',
    rook: 'Castle',
    bishop: 'Bishop',
    knight: 'Knight',
    pawn: 'Pawn'
  };

  const pieceValues = {
    pawn: 1,
    knight: 3,
    bishop: 3,
    rook: 5,
    queen: 9,
    king: 1000
  };

  function getValidMoves(row, col, piece, boardState = board) {
    const moves = [];
    const { type, color } = piece;

    switch (type) {
      case 'pawn':
        const direction = color === 'white' ? -1 : 1;
        const startRow = color === 'white' ? 6 : 1;
        
        // Forward move
        if (boardState[row + direction]?.[col] === null) {
          moves.push([row + direction, col]);
          // Double move from start
          if (row === startRow && boardState[row + 2 * direction]?.[col] === null) {
            moves.push([row + 2 * direction, col]);
          }
        }
        
        // Captures
        [-1, 1].forEach(offset => {
          const target = boardState[row + direction]?.[col + offset];
          if (target && target.color !== color) {
            moves.push([row + direction, col + offset]);
          }
        });
        break;

      case 'rook':
        [[0, 1], [0, -1], [1, 0], [-1, 0]].forEach(([dr, dc]) => {
          for (let i = 1; i < 8; i++) {
            const newRow = row + dr * i;
            const newCol = col + dc * i;
            if (newRow < 0 || newRow > 7 || newCol < 0 || newCol > 7) break;
            const target = boardState[newRow][newCol];
            if (!target) {
              moves.push([newRow, newCol]);
            } else {
              if (target.color !== color) moves.push([newRow, newCol]);
              break;
            }
          }
        });
        break;

      case 'knight':
        [[2, 1], [2, -1], [-2, 1], [-2, -1], [1, 2], [1, -2], [-1, 2], [-1, -2]].forEach(([dr, dc]) => {
          const newRow = row + dr;
          const newCol = col + dc;
          if (newRow >= 0 && newRow < 8 && newCol >= 0 && newCol < 8) {
            const target = boardState[newRow][newCol];
            if (!target || target.color !== color) {
              moves.push([newRow, newCol]);
            }
          }
        });
        break;

      case 'bishop':
        [[1, 1], [1, -1], [-1, 1], [-1, -1]].forEach(([dr, dc]) => {
          for (let i = 1; i < 8; i++) {
            const newRow = row + dr * i;
            const newCol = col + dc * i;
            if (newRow < 0 || newRow > 7 || newCol < 0 || newCol > 7) break;
            const target = boardState[newRow][newCol];
            if (!target) {
              moves.push([newRow, newCol]);
            } else {
              if (target.color !== color) moves.push([newRow, newCol]);
              break;
            }
          }
        });
        break;

      case 'queen':
        [[0, 1], [0, -1], [1, 0], [-1, 0], [1, 1], [1, -1], [-1, 1], [-1, -1]].forEach(([dr, dc]) => {
          for (let i = 1; i < 8; i++) {
            const newRow = row + dr * i;
            const newCol = col + dc * i;
            if (newRow < 0 || newRow > 7 || newCol < 0 || newCol > 7) break;
            const target = boardState[newRow][newCol];
            if (!target) {
              moves.push([newRow, newCol]);
            } else {
              if (target.color !== color) moves.push([newRow, newCol]);
              break;
            }
          }
        });
        break;

      case 'king':
        [[0, 1], [0, -1], [1, 0], [-1, 0], [1, 1], [1, -1], [-1, 1], [-1, -1]].forEach(([dr, dc]) => {
          const newRow = row + dr;
          const newCol = col + dc;
          if (newRow >= 0 && newRow < 8 && newCol >= 0 && newCol < 8) {
            const target = boardState[newRow][newCol];
            if (!target || target.color !== color) {
              moves.push([newRow, newCol]);
            }
          }
        });
        break;
    }

    return moves;
  }

  function evaluateBoard(boardState) {
    let score = 0;
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 8; col++) {
        const piece = boardState[row][col];
        if (piece) {
          const value = pieceValues[piece.type];
          score += piece.color === currentPlayer ? value : -value;
        }
      }
    }
    return score;
  }

  function getAllPossibleMoves(color, boardState) {
    const moves = [];
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 8; col++) {
        const piece = boardState[row][col];
        if (piece && piece.color === color) {
          const pieceMoves = getValidMoves(row, col, piece, boardState);
          pieceMoves.forEach(([toRow, toCol]) => {
            moves.push({ from: [row, col], to: [toRow, toCol], piece });
          });
        }
      }
    }
    return moves;
  }

  function simulateMove(move, boardState) {
    const newBoard = boardState.map(row => [...row]);
    const [fromRow, fromCol] = move.from;
    const [toRow, toCol] = move.to;
    newBoard[toRow][toCol] = newBoard[fromRow][fromCol];
    newBoard[fromRow][fromCol] = null;
    return newBoard;
  }

  function makeComputerMove() {
    const opponentColor = playerColor === 'white' ? 'black' : 'white';
    const possibleMoves = getAllPossibleMoves(opponentColor, board);
    
    if (possibleMoves.length === 0) {
      setIsThinking(false);
      return;
    }

    let selectedMove;

    if (difficulty === 1) {
      // Random move
      selectedMove = possibleMoves[Math.floor(Math.random() * possibleMoves.length)];
    } else if (difficulty === 2) {
      // Prefer captures
      const captureMoves = possibleMoves.filter(move => {
        const [toRow, toCol] = move.to;
        return board[toRow][toCol] !== null;
      });
      if (captureMoves.length > 0 && Math.random() > 0.3) {
        selectedMove = captureMoves[Math.floor(Math.random() * captureMoves.length)];
      } else {
        selectedMove = possibleMoves[Math.floor(Math.random() * possibleMoves.length)];
      }
    } else if (difficulty === 3) {
      // Look for best immediate capture or random
      const scoredMoves = possibleMoves.map(move => {
        const [toRow, toCol] = move.to;
        const capturedPiece = board[toRow][toCol];
        const score = capturedPiece ? pieceValues[capturedPiece.type] : 0;
        return { move, score };
      });
      scoredMoves.sort((a, b) => b.score - a.score);
      selectedMove = scoredMoves[0].move;
    } else if (difficulty === 4) {
      // Look ahead 1 move
      const scoredMoves = possibleMoves.map(move => {
        const newBoard = simulateMove(move, board);
        const [toRow, toCol] = move.to;
        const capturedPiece = board[toRow][toCol];
        const captureValue = capturedPiece ? pieceValues[capturedPiece.type] : 0;
        return { move, score: captureValue + Math.random() * 2 };
      });
      scoredMoves.sort((a, b) => b.score - a.score);
      selectedMove = scoredMoves[0].move;
    } else {
      // Difficulty 5: Look ahead 2 moves with better evaluation
      const scoredMoves = possibleMoves.map(move => {
        const newBoard = simulateMove(move, board);
        const [toRow, toCol] = move.to;
        const capturedPiece = board[toRow][toCol];
        const captureValue = capturedPiece ? pieceValues[capturedPiece.type] : 0;
        
        // Look at opponent's best response
        const opponentResponses = getAllPossibleMoves(playerColor, newBoard);
        let worstResponse = 0;
        opponentResponses.forEach(responseMove => {
          const [respToRow, respToCol] = responseMove.to;
          const respCaptured = newBoard[respToRow][respToCol];
          if (respCaptured) {
            worstResponse = Math.max(worstResponse, pieceValues[respCaptured.type]);
          }
        });
        
        return { move, score: captureValue - worstResponse * 0.5 + Math.random() };
      });
      scoredMoves.sort((a, b) => b.score - a.score);
      selectedMove = scoredMoves[0].move;
    }

    const [fromRow, fromCol] = selectedMove.from;
    const [toRow, toCol] = selectedMove.to;
    
    const newBoard = board.map(row => [...row]);
    const piece = newBoard[fromRow][fromCol];
    const capturedPiece = newBoard[toRow][toCol];
    
    if (capturedPiece) {
      setCapturedPieces(prev => ({
        ...prev,
        [opponentColor]: [...prev[opponentColor], capturedPiece]
      }));
      setMessage(`The wild thing captured your ${pieceNames[capturedPiece.type]}! 😱`);
    } else {
      setMessage(`Wild thing moved their ${pieceNames[piece.type]}. Your turn!`);
    }
    
    newBoard[toRow][toCol] = piece;
    newBoard[fromRow][fromCol] = null;
    
    setBoard(newBoard);
    setCurrentPlayer(playerColor);
    setIsThinking(false);
  }

  function handleDragStart(e, row, col) {
    const piece = board[row][col];
    if (!piece || piece.color !== playerColor || isThinking) {
      e.preventDefault();
      return;
    }
    
    setSelectedSquare([row, col]);
    const moves = getValidMoves(row, col, piece);
    setValidMoves(moves);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', `${row},${col}`);
  }

  function handleDragOver(e) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }

  function handleDrop(e, toRow, toCol) {
    e.preventDefault();
    const [fromRow, fromCol] = e.dataTransfer.getData('text/plain').split(',').map(Number);
    
    const isValidMove = validMoves.some(([r, c]) => r === toRow && c === toCol);
    
    if (isValidMove) {
      const newBoard = board.map(row => [...row]);
      const piece = newBoard[fromRow][fromCol];
      const capturedPiece = newBoard[toRow][toCol];
      
      if (capturedPiece) {
        setCapturedPieces(prev => ({
          ...prev,
          [playerColor]: [...prev[playerColor], capturedPiece]
        }));
        setMessage(`Great move! You captured a ${pieceNames[capturedPiece.type]}! 🎉`);
      } else {
        setMessage(`Nice move with your ${pieceNames[piece.type]}!`);
      }
      
      newBoard[toRow][toCol] = piece;
      newBoard[fromRow][fromCol] = null;
      
      setBoard(newBoard);
      setCurrentPlayer(playerColor === 'white' ? 'black' : 'white');
    }
    
    setSelectedSquare(null);
    setValidMoves([]);
  }

  function startGame() {
    setBoard(initializeBoard());
    setCurrentPlayer('white');
    setMessage(playerColor === 'white' ? 'Your turn! Drag a piece to move it.' : 'Wild thing is thinking...');
    setSelectedSquare(null);
    setValidMoves([]);
    setCapturedPieces({ white: [], black: [] });
    setGameStarted(true);
    setIsThinking(false);
  }

  function resetGame() {
    setGameStarted(false);
    setBoard(null);
    setMessage('');
    setSelectedSquare(null);
    setValidMoves([]);
    setCapturedPieces({ white: [], black: [] });
  }

  const isValidMoveSquare = (row, col) => {
    return validMoves.some(([r, c]) => r === row && c === col);
  };

  if (!gameStarted) {
    return (
      <div className="min-h-screen relative overflow-hidden bg-gradient-to-b from-amber-100 via-orange-50 to-amber-200">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-10 left-10 text-8xl">🌲</div>
          <div className="absolute top-20 right-20 text-7xl">🌳</div>
          <div className="absolute bottom-10 left-20 text-9xl">🌲</div>
          <div className="absolute bottom-20 right-10 text-8xl">🌳</div>
          <div className="absolute top-40 left-1/4 text-6xl">⭐</div>
          <div className="absolute bottom-40 right-1/4 text-5xl">🌙</div>
        </div>

        <div className="relative z-10 max-w-2xl mx-auto p-8 min-h-screen flex items-center justify-center">
          <div className="bg-amber-50 p-8 rounded-3xl shadow-2xl border-8 border-amber-900 w-full">
            <h1 className="text-5xl font-bold text-black mb-6 text-center" style={{ fontFamily: 'Georgia, serif' }}>
              Play Chess
            </h1>
            <div className="space-y-6">
              <div>
                <label className="block text-2xl font-bold text-grey mb-3">
                  Choose Your Color:
                </label>
                <div className="flex gap-4">
                  <button
                    onClick={() => setPlayerColor('white')}
                    className={`flex-1 py-4 px-6 rounded-xl text-5xl font-bold transition-all ${
                      playerColor === 'white'
                        ? 'bg-amber-600 text-white shadow-lg scale-105'
                        : 'bg-amber-200 text-amber-900 hover:bg-amber-300'
                    }`}
                  >
                    ♔ White
                  </button>
                  <button
                    onClick={() => setPlayerColor('black')}
                    className={`flex-1 py-4 px-6 rounded-xl text-5xl font-bold transition-all ${
                      playerColor === 'black'
                        ? 'bg-amber-600 text-white shadow-lg scale-105'
                        : 'bg-amber-200 text-amber-900 hover:bg-amber-300'
                    }`}
                  >
                    ♚ Black
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-2xl font-bold text-amber-900 mb-3">
                  Difficulty: {difficulty}
                </label>
                <input
                  type="range"
                  min="1"
                  max="5"
                  value={difficulty}
                  onChange={(e) => setDifficulty(parseInt(e.target.value))}
                  className="w-full h-3 bg-amber-200 rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between text-sm text-amber-700 mt-2">
                  <span>Easy</span>
                  <span>Hard</span>
                </div>
                <p className="text-amber-800 mt-2 text-center">
                  {difficulty === 1 && "🐣 Random moves - perfect for beginners!"}
                  {difficulty === 2 && "🦊 Likes to capture pieces sometimes"}
                  {difficulty === 3 && "🐺 Hunts for the best captures"}
                  {difficulty === 4 && "🦁 Thinks ahead and plans moves"}
                  {difficulty === 5 && "👑 A true wild chess master!"}
                </p>
              </div>

              <button
                onClick={startGame}
                className="w-full bg-amber-600 hover:bg-amber-700 text-white font-bold py-6 px-8 rounded-2xl text-6xl shadow-lg border-4 border-amber-800 transition-all hover:scale-105"
              >
                🎮 Start Playing
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-b from-amber-100 via-orange-50 to-amber-200">
      {/* Wild forest background elements */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-10 left-10 text-8xl">🌲</div>
        <div className="absolute top-20 right-20 text-7xl">🌳</div>
        <div className="absolute bottom-10 left-20 text-9xl">🌲</div>
        <div className="absolute bottom-20 right-10 text-8xl">🌳</div>
        <div className="absolute top-40 left-1/4 text-6xl">⭐</div>
        <div className="absolute bottom-40 right-1/4 text-5xl">🌙</div>
      </div>

      <div className="relative z-10 max-w-6xl mx-auto p-4">
        {/* Title */}
        <div className="text-center mb-4">
          <h1 className="text-4xl font-bold text-amber-900 mb-2" style={{ fontFamily: 'Georgia, serif' }}>
            Where the Wild Chess Is
          </h1>
          <p className="text-lg text-amber-800">
            Playing as {playerColor === 'white' ? '♔ White' : '♚ Black'} • Difficulty: {difficulty}
          </p>
        </div>

        {/* Message board */}
        <div className="bg-amber-800 text-amber-50 p-4 rounded-2xl mb-4 text-center shadow-lg border-4 border-amber-900">
          <p className="text-2xl font-bold">{message}</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-6 items-start justify-center">
          {/* Captured pieces - Black */}
          <div className="bg-slate-800 p-4 rounded-2xl shadow-lg border-4 border-slate-900 min-w-[120px]">
            <h3 className="text-amber-50 font-bold mb-2 text-center">Captured</h3>
            <div className="flex flex-wrap gap-1">
              {capturedPieces.white.map((piece, i) => (
                <span key={i} className="text-3xl">{pieceEmojis[piece.type][piece.color]}</span>
              ))}
            </div>
          </div>

          {/* Chess board */}
          <div className="bg-slate-900 p-6 rounded-3xl shadow-2xl border-8 border-slate-950">
            <div className="grid grid-cols-8 gap-0 border-4 border-slate-950 shadow-xl">
              {board.map((row, rowIndex) => (
                row.map((piece, colIndex) => {
                  const isLight = (rowIndex + colIndex) % 2 === 0;
                  const isSelected = selectedSquare && selectedSquare[0] === rowIndex && selectedSquare[1] === colIndex;
                  const isValidMove = isValidMoveSquare(rowIndex, colIndex);
                  
                  return (
                    <div
                      key={`${rowIndex}-${colIndex}`}
                      className={`
                        w-16 h-16 flex items-center justify-center text-5xl cursor-pointer
                        transition-all duration-200 relative
                        ${isLight ? 'bg-slate-100' : 'bg-slate-400'}
                        ${isSelected ? 'ring-4 ring-yellow-400' : ''}
                        ${isValidMove ? 'ring-4 ring-emerald-400 animate-pulse' : ''}
                        ${isThinking ? 'pointer-events-none' : ''}
                      `}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, rowIndex, colIndex)}
                    >
                      {piece && (
                        <div
                          draggable={!isThinking}
                          onDragStart={(e) => handleDragStart(e, rowIndex, colIndex)}
                          className={`${isThinking ? 'cursor-not-allowed' : 'cursor-move'} hover:scale-110 transition-transform`}
                        >
                          {pieceEmojis[piece.type][piece.color]}
                        </div>
                      )}
                      {isValidMove && !piece && (
                        <div className="w-4 h-4 bg-emerald-400 rounded-full opacity-70"></div>
                      )}
                    </div>
                  );
                })
              ))}
            </div>
          </div>

          {/* Captured pieces - White */}
          <div className="bg-amber-50 p-4 rounded-2xl shadow-lg border-4 border-amber-200 min-w-[120px]">
            <h3 className="text-amber-900 font-bold mb-2 text-center">Captured</h3>
            <div className="flex flex-wrap gap-1">
              {capturedPieces.black.map((piece, i) => (
                <span key={i} className="text-3xl">{pieceEmojis[piece.type][piece.color]}</span>
              ))}
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="mt-6 text-center">
          <button
            onClick={resetGame}
            className="bg-amber-600 hover:bg-amber-700 text-white font-bold py-3 px-8 rounded-full text-xl shadow-lg border-4 border-amber-800 transition-all hover:scale-105"
          >
            🔄 New Game
          </button>
        </div>
      </div>
    </div>
  );
};

export default WildChessApp;